import type { ComponentProps, PropsWithChildren } from "react";
import { AlertDialog } from "radix-ui";
import type { VariantProps } from "tailwind-variants";
import { tv } from "tailwind-variants";

import { AnimatedBackground, Layout } from "@/shared/ui/common";
import { Button } from "@/shared/ui/common/button";
import { Heading } from "@/shared/ui/common/heading";
import { ScrollArea } from "@/shared/ui/common/scroll-area";
import type { SvgSlot } from "@/shared/ui/common/svg-slot";
import { CircleIcon } from "@/shared/ui/icons";

const styles = tv({
  slots: {
    overlay:
      "fixed inset-0 z-50 size-full [--header-height:5rem] data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0",
    content: "size-full",
    scrollArea: "[&>div]:h-full",
    layout: "flex h-full flex-col gap-2",
    body: "flex grow flex-col items-center justify-center place-self-center sm:max-w-xl sm:gap-12 sm:pb-[var(--header-height)]",
    header:
      "flex grow flex-col items-center justify-center gap-8 px-5 pb-[var(--header-height)] sm:grow-0 sm:px-0 sm:pb-0",
    headerContent: "flex flex-col items-center gap-6 text-center",
    description: "text-xl",
    footer:
      "sticky bottom-0 -mx-5 flex w-full flex-col-reverse gap-4 border-t border-secondary-200 px-5 py-6 sm:static sm:flex-row sm:justify-center sm:border-0 sm:p-0",
    button: "min-w-52",
  },
  variants: {
    theme: {
      dark: {
        overlay:
          "bg-primary-950 [&_[data-slot=alert-description]]:text-primary-50 [&_[data-slot=alert-footer]]:bg-primary-950 sm:[&_[data-slot=alert-footer]]:bg-transparent [&_[data-slot=alert-title]]:text-primary-50",
      },
      light: {
        overlay:
          "bg-primary-50 [&_[data-slot=alert-description]]:text-neutral-800 [&_[data-slot=alert-footer]]:bg-primary-50 sm:[&_[data-slot=alert-footer]]:bg-transparent [&_[data-slot=alert-title]]:text-neutral-950",
      },
    },
  },
});

const {
  content,
  scrollArea,
  layout,
  body,
  header,
  headerContent,
  description,
  footer,
  button,
} = styles();

const Root = ({ ...props }: ComponentProps<typeof AlertDialog.Root>) => {
  return <AlertDialog.Root data-slot="alert-root" {...props} />;
};

const Content = ({
  theme = "dark",
  children,
  ...props
}: ComponentProps<typeof AlertDialog.Content> &
  VariantProps<typeof styles>) => {
  const { overlay } = styles({ theme });

  return (
    <AlertDialog.Portal>
      <AlertDialog.Overlay className={overlay()}>
        <AnimatedBackground theme={theme} />

        <AlertDialog.Content className={content()} {...props}>
          <ScrollArea className={scrollArea()}>
            <div className={layout()}>
              <Layout.Header theme={theme} />

              <div className={body()}>{children}</div>
            </div>
          </ScrollArea>
        </AlertDialog.Content>
      </AlertDialog.Overlay>
    </AlertDialog.Portal>
  );
};

const Header = ({
  children,
  icon,
  ...props
}: Omit<ComponentProps<"div">, "className"> & { icon?: typeof SvgSlot }) => {
  return (
    <div data-slot="alert-header" className={header()} {...props}>
      {icon && <CircleIcon data-slot="alert-icon" size="lg" icon={icon} />}

      <div className={headerContent()}>{children}</div>
    </div>
  );
};

const Title = ({ ...props }: PropsWithChildren) => {
  return (
    <AlertDialog.Title asChild>
      <Heading level={2} size="xx-large" data-slot="alert-title" {...props} />
    </AlertDialog.Title>
  );
};

const Description = ({
  ...props
}: ComponentProps<typeof AlertDialog.Description>) => {
  return (
    <AlertDialog.Description
      data-slot="alert-description"
      className={description()}
      {...props}
    />
  );
};

const Footer = ({ className, ...props }: ComponentProps<"div">) => {
  return (
    <div
      data-slot="alert-footer"
      className={footer({ className })}
      {...props}
    />
  );
};

const Cancel = ({
  ...props
}: Omit<ComponentProps<typeof Button>, "className">) => {
  return (
    <AlertDialog.Cancel data-slot="alert-cancel" asChild>
      <Button size="lg" className={button()} {...props} />
    </AlertDialog.Cancel>
  );
};

const Action = ({
  ...props
}: Omit<ComponentProps<typeof Button>, "className">) => {
  return (
    <AlertDialog.Action data-slot="alert-action" asChild>
      <Button size="lg" className={button()} {...props} />
    </AlertDialog.Action>
  );
};

export const FullscreenAlert = {
  Root,
  Content,
  Header,
  Title,
  Description,
  Footer,
  Cancel,
  Action,
};
